import React, { useState } from 'react';
import styled from 'styled-components';
import { FaArrowRight, FaUser } from 'react-icons/fa';
import { Input } from '../../../components/ui/Input/Input';
import { Button } from '../../../components/ui/Button/Button';
import { colors, breakpoints } from '../../../assets/styles/GlobalStyles';
import { formatCPFCNPJ, unformat } from '../../../utils/formatters';
import { validateDocument } from '../../../utils/validators';
import { resetSenhaService } from '../../auth/services/passwordService';
import logger from '../../../utils/logger';
import { destacarPendencias } from '../../../utils/destaqueCampos';

const Form = styled.form`
  width: 100%;
`;

const InfoBox = styled.div`
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.borderLight};
  border-radius: 8px;
  padding: 1rem;
  margin: 1.5rem 0;
  font-size: 0.9rem;
  color: ${colors.textMuted};
  line-height: 1.5;

  strong {
    color: ${colors.darkBlue};
  }

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.8rem;
  }
`;

/**
 * Formulário de solicitação de recuperação de senha (etapa 1): recebe o
 * CPF/CNPJ, valida o formato e aciona o backend via resetSenhaService.
 * Em caso de sucesso, repassa o documento (sem máscara) e os dados da
 * resposta (e-mail mascarado, minutos de validade) para o passo 2.
 * `onTrocarEmail` abre a troca do e-mail de recuperação com o documento já
 * digitado (sem máscara).
 */
export const ForgotPasswordForm = ({ documentoInicial = '', onSuccess, onError, onTrocarEmail }) => {
  const [documento, setDocumento] = useState(formatCPFCNPJ(documentoInicial));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDocumentoChange = (e) => {
    const formatted = formatCPFCNPJ(e.target.value);
    setDocumento(formatted);
    if (error) setError('');
  };

  const validate = () => {
    const documentoNumeros = unformat(documento);

    if (!documentoNumeros) {
      setError('Por favor, informe seu CPF ou CNPJ');
      return false;
    }

    if (!validateDocument(documentoNumeros)) {
      setError('CPF ou CNPJ inválido');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      destacarPendencias(['documento']);
      return;
    }

    setLoading(true);
    setError('');

    const documentoNumeros = unformat(documento);

    try {
      const resultado = await resetSenhaService.solicitarReset(documentoNumeros);
      onSuccess(documentoNumeros, resultado.data);
    } catch (err) {
      logger.error('Erro no reset:', err);
      setError(err.message || 'Erro ao processar solicitação');
      onError(err, documentoNumeros);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} noValidate>
      <div data-field-name="documento">
        <Input
          label="CPF/CNPJ"
          type="text"
          value={documento}
          onChange={handleDocumentoChange}
          error={error}
          icon={<FaUser />}
          placeholder="000.000.000-00"
          maxLength={18}
          required
          disabled={loading}
          helper="Digite o CPF ou CNPJ usado no login"
        />
      </div>

      <InfoBox>
        <strong>📧 Como funciona:</strong> enviaremos um código de verificação
        de 6 dígitos para o e-mail de recuperação cadastrado. O código é
        válido por alguns minutos.
      </InfoBox>

      <Button
        type="submit"
        variant="primary"
        size="full"
        disabled={loading}
        rightIcon={<FaArrowRight />}
      >
        {loading ? 'Enviando...' : 'Recuperar senha'}
      </Button>

      {onTrocarEmail && (
        <Button
          type="button"
          variant="ghost"
          size="full"
          onClick={() => onTrocarEmail(unformat(documento))}
          disabled={loading}
        >
          Não tem acesso ao e-mail de recuperação? Cadastrar ou trocar e-mail
        </Button>
      )}
    </Form>
  );
};
